import {useField, useFormikContext} from 'formik';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useGetCabinetTopsQuery} from 'components/customer/Room/store/roomApi';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {MaterialType} from 'components/customer/Materials/entity';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {produce} from 'immer';
import Excel from 'shared/Excel';
import {Drawer} from 'components/customer/Product/entity/Drawer';
import {
    setCarcaseThickness,
    setIsRailOnEdge,
    setIsRailOnFlat,
    setRailWidth,
} from 'components/customer/Product/store/productSlice';
import {isEqual, pick} from 'lodash';

export const useInnerDrawerPosition = (isInnerDrawer = false) => {
    const dispatch = useAppDispatch();
    const {setFieldValue} = useFormikContext();

    const [{value: drawerAmount}] = useField<number>({name: 'drawer_amount'});
    const [{value: drawers}] = useField<Drawer[]>({
        name: 'drawers',
    });
    const [{value: cabinetTop}] = useField<number>({name: 'cabinet_top'});
    const [{value: cabinetHeight}] = useField<number>({name: 'cabinet_height'});

    const carcaseMaterial = useAppSelector((state) =>
        getMaterial(state, MaterialType.CARCASE)
    );

    const [searchParams] = useSearchParams();
    const {data: drawerSystems} = useGetDrawerSystemQuery();
    const {data: cabinetTops} = useGetCabinetTopsQuery();

    const isEditingProduct = useMemo(() => {
        return searchParams.has('cabinetId');
    }, [searchParams]);

    const getTotalAvailableHeight = useCallback(
        (drawers: Drawer[]) => {
            if (drawers && drawers.length && cabinetTops && drawerSystems) {
                const customerTouchedDrawers = drawers.filter(
                    (drawer) => drawer.touched
                );
                const touchedHeight = customerTouchedDrawers.reduce((a, b) => {
                    return a + b.drawer_face_height;
                }, 0);

                const top = cabinetTops.find((top) => top.id == cabinetTop);
                const heightFormula =
                    'cabinet_height - cabinet_carc_colour.thickness - rail.height';

                const scope = {
                    cabinet_height: cabinetHeight,
                    cabinet_carc_colour: {
                        thickness: 0,
                    },
                    rail: {
                        height: 0,
                    },
                };

                // bottom carcase material thickness
                if (carcaseMaterial) {
                    scope.cabinet_carc_colour.thickness =
                        carcaseMaterial.thickness;
                }

                if (top) {
                    // top carcase material thickness
                    if (top.name.toLowerCase().includes('rail on flat')) {
                        scope.rail.height = carcaseMaterial.thickness;
                    }

                    // top rail width
                    if (top.name.toLowerCase().includes('rail on edge')) {
                        scope.rail.height = Number(top?.widthFunction || 0);
                    }
                }

                const availableHeight = Excel.calculate<number>(
                    heightFormula,
                    scope
                );

                return availableHeight - touchedHeight;
            }
        },
        [
            cabinetHeight,
            cabinetTop,
            drawers,
            cabinetTops,
            drawerSystems,
            carcaseMaterial,
        ]
    );

    const calculatePosition = useCallback(
        (
            drawer: Drawer,
            index: number,
            usedHeight: number,
            availableHeight: number
        ) => {
            if (drawerSystems) {
                const drawerSystem = drawerSystems.find(
                    (system) => system.id == drawer.insert_id
                );

                let position = 0;
                let updatedUsedHeight = 0;

                if (drawerSystem) {
                    if (index == drawerAmount - 1) {
                        position =
                            drawerSystem?.innerDrawerBottomClearance || 0;

                        updatedUsedHeight = availableHeight;
                    } else {
                        position = Number(
                            (
                                usedHeight + drawerSystem.bottomClearance || 0
                            ).toFixed(2)
                        );

                        updatedUsedHeight = usedHeight + availableHeight;
                    }
                }

                return {
                    position,
                    updatedUsedHeight,
                };
            }
        },
        [
            drawerSystems,
            cabinetTops,
            drawerAmount,
            cabinetTop,
            getTotalAvailableHeight,
        ]
    );

    const selectProps = useCallback((drawers: Drawer[]) => {
        const propsToCompare = [
            'position_to_bottom',
            'insert_id',
            'systemBottomClearance',
            'systemInnerDrawerBottomClearance',
            'systemTopClearance',
        ];

        return drawers.map((drawer) => pick(drawer, propsToCompare));
    }, []);

    const localCabinetHeight = useRef(cabinetHeight);
    const localDrawerAmount = useRef(drawerAmount);
    const localCabinetTop = useRef(cabinetTop);
    const localCarcaseMaterial = useRef(carcaseMaterial);
    const localDrawers = useRef(drawers);
    const initialCalculation = useRef(true);

    const resetDrawers = useCallback(
        (drawerAmount: number) => {
            if (!isInnerDrawer) return;

            let touchedDrawers = drawers.filter(
                (drawer) => drawer.touched
            ).length;

            if (drawerAmount != localDrawerAmount.current) {
                localDrawerAmount.current = drawerAmount;
                const newDrawers = produce(drawers, (draft) => {
                    const drawerCountDiff = drawerAmount - draft.length; // ensures we capture manual input
                    if (drawerCountDiff > 0) {
                        const drawerCopy = draft[draft.length - 1];
                        for (let i = 0; i < drawerCountDiff; i++) {
                            draft.push({
                                ...drawerCopy,
                                touched: false,
                            });
                        }
                    } else if (drawerCountDiff < 0) {
                        draft.splice(drawerCountDiff);
                        touchedDrawers = draft.filter(
                            (drawer) => drawer.touched
                        ).length;
                    }

                    let usedHeight = 0;
                    const availableHeight = getTotalAvailableHeight(draft);

                    for (let i = draft.length - 1; i >= 0; i--) {
                        const drawer = draft[Number(i)];
                        if (drawer?.touched) {
                            usedHeight += drawer.drawer_face_height;
                            return;
                        }

                        const {position, updatedUsedHeight} = calculatePosition(
                            drawer,
                            i,
                            usedHeight,
                            availableHeight / (drawerAmount - touchedDrawers)
                        );

                        drawer.drawer_face_height =
                            availableHeight / (drawerAmount - touchedDrawers);
                        drawer.position_to_bottom = position;

                        usedHeight = updatedUsedHeight;
                    }
                });

                localDrawers.current = newDrawers;
                // skip validation as this will be validated once the runners are set based on the new positions
                void setFieldValue('drawers', newDrawers, false);
            } else {
                if (
                    localCabinetHeight.current != cabinetHeight ||
                    localCabinetTop.current != cabinetTop ||
                    !isEqual(localCarcaseMaterial.current, carcaseMaterial) ||
                    !isEqual(
                        selectProps(localDrawers.current),
                        selectProps(drawers)
                    )
                ) {
                    localCabinetHeight.current = cabinetHeight;
                    localCabinetTop.current = cabinetTop;
                    localCarcaseMaterial.current = carcaseMaterial;

                    if (isEditingProduct) {
                        return;
                    }

                    if (!initialCalculation.current) {
                        return;
                    }
                    initialCalculation.current = false;

                    let usedHeight = 0;
                    const availableHeight = getTotalAvailableHeight(drawers);

                    const newDrawers = produce(drawers, (draft) => {
                        for (let i = draft.length - 1; i >= 0; i--) {
                            const drawer = draft[Number(i)];
                            if (drawer?.touched) {
                                usedHeight += drawer.drawer_face_height;
                                return;
                            }

                            const {position, updatedUsedHeight} =
                                calculatePosition(
                                    drawer,
                                    i,
                                    usedHeight,
                                    availableHeight /
                                        (drawerAmount - touchedDrawers)
                                );

                            drawer.drawer_face_height =
                                availableHeight /
                                (drawerAmount - touchedDrawers);
                            drawer.position_to_bottom = position;

                            usedHeight = updatedUsedHeight;
                        }
                    });

                    localDrawers.current = newDrawers;
                    void setFieldValue('drawers', newDrawers);
                }
            }
        },
        [
            drawers,
            setFieldValue,
            isInnerDrawer,
            cabinetHeight,
            cabinetTop,
            carcaseMaterial,
            calculatePosition,
            selectProps,
            isEditingProduct,
        ]
    );

    useEffect(() => {
        resetDrawers(drawerAmount);
    }, [drawerAmount, carcaseMaterial, cabinetTop, drawers]);

    useEffect(() => {
        resetDrawers(drawerAmount);
    }, [cabinetHeight]);

    useEffect(() => {
        if (cabinetTops && cabinetTop != undefined) {
            const selectedTop = cabinetTops.find((top) => top.id == cabinetTop);

            if (selectedTop) {
                // top carcase material thickness
                if (selectedTop.name.toLowerCase().includes('rail on flat')) {
                    dispatch(setIsRailOnFlat(true));
                    dispatch(setIsRailOnEdge(false));
                } else if (
                    selectedTop.name.toLowerCase().includes('rail on edge')
                ) {
                    dispatch(setIsRailOnEdge(true));
                    dispatch(setIsRailOnFlat(false));
                    if (!isNaN(Number(selectedTop.widthFunction))) {
                        dispatch(
                            setRailWidth(Number(selectedTop.widthFunction))
                        );
                    }
                } else {
                    dispatch(setIsRailOnEdge(false));
                    dispatch(setIsRailOnFlat(false));
                }
            }
        }
    }, [cabinetTops, cabinetTop]);

    useEffect(() => {
        if (carcaseMaterial) {
            dispatch(setCarcaseThickness(carcaseMaterial?.thickness));
        }
    }, [carcaseMaterial]);
};
